import React from 'react';
import { isEditableElement, KeyAttributeValue } from '@hawkins/helpers';
// Useful info about anchor tags: https://stackoverflow.com/a/11145021.
function isAnchorLink(target) {
    return target.tagName === 'A' && target.hasAttribute('href');
}
// We want to rely on native browser behavior as much as possible.
// This function tries to guess whether the browser will trigger a native click event (`onClick`)--
// if it does, then we should NOT handle `onKeyDown/onKeyUp`.
//
// Conversely, if the browser does NOT trigger a native click event (`onClick`),
// then we need to put custom logic inside `onKeyDown/onKeyUp`.
function shouldHandleKeyPress(_a) {
    var currentTarget = _a.currentTarget, key = _a.key, target = _a.target;
    // Ignore events that bubble through portals.
    // Check for `Element` rather than `HTMLElement` to allow for `SVGElement`.
    if (!(target instanceof Element) || currentTarget !== target)
        return false;
    if (key !== KeyAttributeValue.ENTER && key !== KeyAttributeValue.SPACE)
        return false;
    var role = target.getAttribute('role');
    // A native button should trigger `onClick` for relevant key presses.
    if (target.tagName === 'BUTTON')
        return false;
    // A native anchor link should trigger `onClick` for `Enter` key presses.
    if (isAnchorLink(target) && (role !== 'button' || key !== KeyAttributeValue.SPACE)) {
        return false;
    }
    // Only `Enter` key presses are relevant for elements with ARIA role "link".
    if (role === 'link' && key !== KeyAttributeValue.ENTER)
        return false;
    // Don't interfere when `Enter` or `Space` is pressed inside an editable element.
    if (isEditableElement(target))
        return false;
    return true;
}
var displayName = 'usePress';
/**
 * A hook that enables a component to be “pressable,”
 * taking into account various input mediums including mouse, keyboard, and touch and
 * adhering to the WAI-ARIA Authoring Practices relevant for the event target’s tag name and/or ARIA role.
 *
 * Inspired by React Aria’s `usePress` hook (https://react-spectrum.adobe.com/react-aria/usePress.html),
 * but we built our own to avoid needing to hack around React Aria’s sometimes intrusive implementation.
 */
export function usePress(_a) {
    var _b = _a.alwaysHandleClick, alwaysHandleClick = _b === void 0 ? false : _b, _c = _a.disabled, disabled = _c === void 0 ? false : _c, onPress = _a.onPress;
    var isPressedRef = React.useRef(false);
    var onClick = React.useMemo(function () {
        if (!onPress && !alwaysHandleClick)
            return undefined;
        return function (ev) {
            // Ignore events that bubble through portals.
            // Check for `Element` rather than `HTMLElement` to allow for `SVGElement`.
            if (!(ev.target instanceof Element) || !ev.currentTarget.contains(ev.target))
                return;
            if (disabled) {
                ev.preventDefault();
                return;
            }
            onPress === null || onPress === void 0 ? void 0 : onPress(ev);
        };
    }, [alwaysHandleClick, disabled, onPress]);
    var onKeyDown = React.useMemo(function () {
        if (!onPress)
            return undefined;
        return function (ev) {
            if (!shouldHandleKeyPress(ev))
                return;
            if (disabled) {
                ev.preventDefault();
                return;
            }
            // Prevent the page from scrolling.
            if (ev.key === KeyAttributeValue.SPACE)
                ev.preventDefault();
            isPressedRef.current = true;
        };
    }, [disabled, onPress]);
    // NOTE: We trigger `onPress` on the `keyup` event rather than `keydown` to avoid potential issues
    // caused by shifting element focus in tandem with inconsistencies in when `onClick` is triggered by native browser elements.
    //
    // In Chrome, it seems that `onClick` is triggered on a native `<button>` element in the following ways:
    //   - mouse click -> `onClick` triggers on mouseup
    //   - `Space` key press -> `onClick` triggers on keyup
    //   - `Enter` key press -> `onClick` triggers on mousedown
    //
    // As an example, let's say that inside an `onPress` handler, focus is programmatically transferred to a separate `<button>`
    // (we do this inside `Menu` for the `closeOnSelect` feature). A bug would occur if `onPress` were triggered on `keydown`
    // due to this order of events:
    //   focus on `ComponentFoo` --> `Enter` keydown on `ComponentFoo` --> `ComponentFoo` `onPress` is called -->
    //   focus programmatically shifts to `<button>` --> `Enter` keyup on `<button>` --> `<button>` `onPress` is called
    var onKeyUp = React.useMemo(function () {
        if (!onPress)
            return undefined;
        return function (ev) {
            if (!shouldHandleKeyPress(ev))
                return;
            // Avoid calling `onPress` if the `keyup` event occurs immediately after focus is transferred from a different element
            // (i.e., `keyup` occurred on this element but `keydown` occurred on a different element).
            if (!isPressedRef.current)
                return;
            isPressedRef.current = false;
            if (disabled) {
                ev.preventDefault();
                return;
            }
            // NOTE: To make life easier for consumers, we take a component's `onClick` handler and simply add keyboard support to it
            // when necessary (see the `shouldHandleKeyPress` function). For `onClick`, the expected argument is a MouseEvent.
            // Obviously, this won't be the case if the handler is being triggered by a key press.
            //
            // In most cases, keyboard support won't be necessary (because the component is using an HTML element
            // like `<button>` or `<a>` that natively triggers `onClick` for all relevant interactions), so this won't be an issue.
            // In all other cases, IT IS UP TO THE CONSUMER to be aware that `onClick` could be triggered by a key press
            // and so should not assume that the event argument is a MouseEvent.
            onPress(ev);
        };
    }, [disabled, onPress]);
    var pressProps = {};
    if (onClick)
        pressProps.onClick = onClick;
    if (onKeyDown)
        pressProps.onKeyDown = onKeyDown;
    if (onKeyUp)
        pressProps.onKeyUp = onKeyUp;
    return { pressProps: pressProps };
}
usePress.displayName = displayName;
